import { queryOptions, skipToken } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetLocationsCities200 } from '@eureca/recruiter-api-sdk/types';

export type City = GetLocationsCities200['cities'][number];

// EU-497 RN-04 — cidade com UF resolvido (busca por nome do "Local de trabalho").
// O endpoint ainda não está no SDK gerado; tipamos a shape à mão (forward-compat).
export interface CityWithState {
  id: number;
  name: string;
  stateAcronym: string;
}

/** Rótulo/valor único "Cidade - UF" — desambigua homônimas (Montes Claros - MG
 * vs - GO) e serve tanto de exibição quanto de valor persistido em workplaceCities. */
export function cityWithStateLabel(city: CityWithState): string {
  return city.stateAcronym ? `${city.name} - ${city.stateAcronym}` : city.name;
}

async function searchCities(q: string, signal?: AbortSignal): Promise<CityWithState[]> {
  const res = await fetchClient<{ cities: CityWithState[] }>({
    method: 'GET',
    url: '/locations/cities/search',
    params: { q },
    signal,
  });
  if (res.error) {
    throw new CitiesFetchError(`Failed to search cities "${q}" (status ${res.error.status})`, 0);
  }
  return res.data.cities;
}

/**
 * EU-497 RN-04 — busca de cidade por nome (todas as UFs) para o autocomplete do
 * "Local de trabalho". Gate de 2 chars (< 2 → skipToken, sem request). Debounce
 * fica no container (`useDebouncedValue`), como manda o server-side-search.
 */
export function citiesSearchQueryOptions(term: string) {
  const q = term.trim();
  return queryOptions({
    queryKey: ['recruiter', 'locations', 'cities-search', q] as const,
    queryFn: q.length >= 2 ? ({ signal }) => searchCities(q, signal) : skipToken,
    staleTime: 5 * 60_000,
  });
}

export class CitiesFetchError extends Error {
  readonly name = 'CitiesFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchCities(stateAcronym: string, signal?: AbortSignal): Promise<City[]> {
  const res = await fetchClient<GetLocationsCities200>({
    method: 'GET',
    url: '/locations/cities',
    params: { stateAcronym },
    signal,
  });
  if (res.error) {
    throw new CitiesFetchError(
      `Failed to load cities for ${stateAcronym} (status ${res.error.status})`,
      res.error.status
    );
  }
  return res.data.cities;
}

/**
 * Loads cities for a Brazilian UF. `stateAcronym` is null when the user hasn't
 * picked a state yet — TanStack's `skipToken` short-circuits the query in
 * that case so we don't fire a 400-bound request.
 */
export function citiesByStateQueryOptions(stateAcronym: string | null) {
  return queryOptions({
    queryKey: ['recruiter', 'locations', 'cities', stateAcronym] as const,
    queryFn: stateAcronym ? ({ signal }) => fetchCities(stateAcronym, signal) : skipToken,
    staleTime: 5 * 60_000, // cities don't change in a session
  });
}

/**
 * Juliana QA bug — EU-160. Loads cities for a list of UFs (parallel `GET
 * /locations/cities?stateAcronym=<UF>` per UF, deduplicated by city name).
 * Empty array → skipToken (no request); single UF → equivalent to
 * `citiesByStateQueryOptions(uf)` but normalized to multi-state shape.
 *
 * Multi-state cidade options servem AMBOS os FormFields de cidade
 * (Requisito de localidade — Cidades + Local de trabalho), mas a seleção
 * vive em arrays independentes no form values.
 */
export function citiesByStatesQueryOptions(stateAcronyms: string[]) {
  const normalized = Array.from(new Set(stateAcronyms.filter((s) => s.trim().length === 2)))
    .map((s) => s.toUpperCase())
    .sort();
  return queryOptions({
    queryKey: ['recruiter', 'locations', 'cities-multi', normalized] as const,
    queryFn:
      normalized.length > 0
        ? async ({ signal }) => {
            const results = await Promise.all(normalized.map((uf) => fetchCities(uf, signal)));
            // Deduplicate by city name (preserve first occurrence — Brazilian
            // cities have unique names within a UF, and across-UF collisions
            // are rare enough that the operator can pick either; the array
            // we send back to Core stores a plain string list anyway).
            const seen = new Set<string>();
            const deduped: City[] = [];
            for (const list of results) {
              for (const c of list) {
                if (seen.has(c.name)) continue;
                seen.add(c.name);
                deduped.push(c);
              }
            }
            return deduped;
          }
        : skipToken,
    staleTime: 5 * 60_000,
  });
}
